import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'toolRadio',
    'noToolRadio',
    'fieldTestRadio',
    'byConcernIdsRadio',
    'byConcernIdsLabel',
    'concernSelection',
    'randomizationDescription',
    'defaultRadio',
    'defaultLabel',
    'fileInput',
    'submitButton',
    'fieldTestAddonOption',
    'addFieldTestCheckbox',
    'addFieldTestHidden',
    'noToolHidden',
    'testTypeHidden'
  ]

  connect () {
    this.initializeForm()
  }

  initializeForm () {
    // Always try to update randomization visibility if we have the radio button
    if (this.hasByConcernIdsRadioTarget) {
      this.updateRandomizationVisibility()
    }
    if (this.hasSubmitButtonTarget) {
      this.updateSubmitButton()
    }
    // Initialize the hidden field values
    this.syncNoToolValue()
    this.syncTestTypeValue()
    this.syncFieldTestCheckbox()
  }

  testTypeChanged () {
    this.updateFieldTestOptions()
    this.updateRandomizationVisibility()
    this.updateSubmitButton()
    this.syncNoToolValue()
    this.syncTestTypeValue()
  }

  updateFieldTestOptions () {
    const toolSelected = this.hasToolRadioTarget && this.toolRadioTarget.checked
    const noToolSelected =
      this.hasNoToolRadioTarget && this.noToolRadioTarget.checked

    // Show field test addon option for Tool/No Tool, hide for Field Test Only
    if (this.hasFieldTestAddonOptionTarget) {
      if (toolSelected || noToolSelected) {
        this.fieldTestAddonOptionTarget.classList.remove('hidden')
      } else {
        this.fieldTestAddonOptionTarget.classList.add('hidden')
        // Uncheck when hiding
        if (this.hasAddFieldTestCheckboxTarget) {
          this.addFieldTestCheckboxTarget.checked = false
          this.syncFieldTestCheckbox()
        }
      }
    }
  }

  randomizationStrategyChanged () {
    this.updateRandomizationVisibility()
    this.updateSubmitButton()
  }

  fileInputChanged () {
    this.updateSubmitButton()
  }

  concernSelectionChanged () {
    this.updateSubmitButton()
  }

  syncFieldTestCheckbox () {
    // Sync the checkbox value to the hidden field in the form
    if (
      this.hasAddFieldTestHiddenTarget &&
      this.hasAddFieldTestCheckboxTarget
    ) {
      const isChecked = this.addFieldTestCheckboxTarget.checked
      this.addFieldTestHiddenTarget.value = isChecked ? '1' : '0'
    }
  }

  syncNoToolValue () {
    // Sync the no tool radio button value to the hidden field in the form
    if (this.hasNoToolHiddenTarget && this.hasNoToolRadioTarget) {
      const isNoToolSelected = this.noToolRadioTarget.checked
      this.noToolHiddenTarget.value = isNoToolSelected ? '1' : '0'
    }
  }

  syncTestTypeValue () {
    // Sync the test type radio button value to the hidden field in the form
    if (this.hasTestTypeHiddenTarget) {
      let testTypeValue = 'tool' // default
      if (this.hasToolRadioTarget && this.toolRadioTarget.checked) {
        testTypeValue = 'tool'
      } else if (this.hasNoToolRadioTarget && this.noToolRadioTarget.checked) {
        testTypeValue = 'no_tool'
      } else if (
        this.hasFieldTestRadioTarget &&
        this.fieldTestRadioTarget.checked
      ) {
        testTypeValue = 'field_test_only'
      }
      this.testTypeHiddenTarget.value = testTypeValue
    }
  }

  updateRandomizationVisibility () {
    if (!this.hasByConcernIdsRadioTarget) {
      return
    }

    // Check which test type is selected
    const toolSelected = this.hasToolRadioTarget && this.toolRadioTarget.checked
    const noToolSelected =
      this.hasNoToolRadioTarget && this.noToolRadioTarget.checked
    const fieldTestOnlySelected =
      this.hasFieldTestRadioTarget && this.fieldTestRadioTarget.checked

    if (toolSelected) {
      // Tool testers use default randomization only
      this.setByConcernIdsEnabled(false)
      this.setDefaultEnabled(true)
      this.setRandomizationDescription(
        'Randomization strategy will be "One random vignette per concern type".'
      )
      this.setConcernSelectionVisible(false)

      // Force default option to be selected
      if (this.hasDefaultRadioTarget) {
        this.defaultRadioTarget.checked = true
      }
      this.byConcernIdsRadioTarget.checked = false
    } else if (noToolSelected) {
      // No Tool testers use concern selection only
      this.setByConcernIdsEnabled(true)
      this.setDefaultEnabled(false)
      this.setRandomizationDescription(
        'Randomization strategy will be "One random vignette variation per expected outcome for selected concerns".'
      )

      // Force By Concern Selection to be selected
      this.byConcernIdsRadioTarget.checked = true
      if (this.hasDefaultRadioTarget) {
        this.defaultRadioTarget.checked = false
      }

      this.setConcernSelectionVisible(true)
    } else if (fieldTestOnlySelected) {
      // Disable all randomization options for Field Test Only
      this.setByConcernIdsEnabled(false)
      this.setDefaultEnabled(false)
      this.setRandomizationDescription(
        'Field Test testers will specify their own test scenario.'
      )
      this.setConcernSelectionVisible(false)

      // Uncheck both options when both are disabled
      if (this.hasDefaultRadioTarget) {
        this.defaultRadioTarget.checked = false
      }
      this.byConcernIdsRadioTarget.checked = false
    } else {
      // Default state - enable all options
      this.setByConcernIdsEnabled(true)
      this.setDefaultEnabled(true)
      this.setRandomizationDescription(
        'Choose how to create reliability tests for this tester.'
      )
      this.setConcernSelectionVisible(false)
    }
  }

  setByConcernIdsEnabled (enabled) {
    this.byConcernIdsRadioTarget.disabled = !enabled
    if (this.hasByConcernIdsLabelTarget) {
      this.setLabelEnabled(this.byConcernIdsLabelTarget, enabled)
    }
  }

  setDefaultEnabled (enabled) {
    if (this.hasDefaultRadioTarget) {
      this.defaultRadioTarget.disabled = !enabled
    }
    if (this.hasDefaultLabelTarget) {
      this.setLabelEnabled(this.defaultLabelTarget, enabled)
    }
  }

  // Disabled labels are dimmed with a not-allowed cursor; enabled ones get a pointer
  setLabelEnabled (label, enabled) {
    label.classList.toggle('opacity-50', !enabled)
    label.classList.toggle('cursor-not-allowed', !enabled)
    label.classList.toggle('cursor-pointer', enabled)
  }

  setRandomizationDescription (text) {
    if (this.hasRandomizationDescriptionTarget) {
      this.randomizationDescriptionTarget.textContent = text
    }
  }

  setConcernSelectionVisible (visible) {
    if (this.hasConcernSelectionTarget) {
      this.concernSelectionTarget.classList.toggle('hidden', !visible)
    }
  }

  updateSubmitButton () {
    // Update reliability form submit button (now handles all cases)
    if (this.hasSubmitButtonTarget) {
      const isFormValid = this.isFormValid()

      if (isFormValid) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.classList.remove(
          'opacity-50',
          'cursor-not-allowed'
        )
      } else {
        this.submitButtonTarget.disabled = true
        this.submitButtonTarget.classList.add(
          'opacity-50',
          'cursor-not-allowed'
        )
      }
    }
  }

  isFormValid () {
    // Check if file input is present and has files (for import form)
    if (this.hasFileInputTarget) {
      return this.fileInputTarget.files && this.fileInputTarget.files.length > 0
    }

    // Check which test type is selected
    const fieldTestOnlySelected =
      this.hasFieldTestRadioTarget && this.fieldTestRadioTarget.checked

    // For new form, check text inputs and concern selection
    const emailInput = this.element.querySelector(
      'input[name="reliability_tester[email]"]'
    )
    const firstNameInput = this.element.querySelector(
      'input[name="reliability_tester[first_name]"]'
    )
    const lastNameInput = this.element.querySelector(
      'input[name="reliability_tester[last_name]"]'
    )

    const emailValue = emailInput?.value.trim()
    const firstNameValue = firstNameInput?.value.trim()
    const lastNameValue = lastNameInput?.value.trim()

    // For field-test-only, we don't need randomization strategy or concern selection
    if (fieldTestOnlySelected) {
      return emailValue && firstNameValue && lastNameValue
    }

    // For tool/no-tool tests, check concern selection when needed
    const isConcernSelectionValid =
      !this.hasByConcernIdsRadioTarget ||
      !this.byConcernIdsRadioTarget.checked ||
      (this.byConcernIdsRadioTarget.checked &&
        this.getSelectedConcerns().length > 0)

    return (
      emailValue && firstNameValue && lastNameValue && isConcernSelectionValid
    )
  }

  getSelectedConcerns () {
    return Array.from(
      this.element.querySelectorAll('input[name="concern_ids[]"]:checked')
    )
  }
}
